/**
 * SQLite dashboard templates (S7, remediated S7-R; grown post-S21 for
 * starter-pack coverage). No pre-existing SQL catalog to source from (G8:
 * SQLite has no Session Browser / Performance Dashboard provider) —
 * authored from scratch around SQLite's `pragma_*` table-valued functions,
 * `sqlite_master`, and (for `index_overhead`) the `dbstat` virtual table
 * (compile-time gated on `SQLITE_ENABLE_DBSTAT_VTAB` — present in the
 * better-sqlite3 build this app bundles, verified in `deps/defines.gypi`,
 * but NOT guaranteed in a custom-amalgamation build; where it's missing the
 * template errors loudly with "no such table: dbstat" rather than silently
 * misreporting). SQLite is an
 * embedded, single-process engine with no server sessions, background jobs,
 * backup service, or live query/wait statistics — `sessions`, `performance`,
 * `jobs` and `backup` categories are ALL absent by design, not a gap: every
 * Health-pack template below lives under `indexes` because that's the only
 * live, actionable signal an embedded file can expose without a running
 * server to sample.
 *
 * S7-R M19 originally cut three templates down to 4 (below the plan's
 * original "5-6" target — flagged as a disputed count in the S7-R report,
 * not silently reconciled by adding filler): `foreign_key_violations` (full
 * scan on interval refresh — STILL cut here, unchanged), `tables_without_index`
 * ("every query is a full table scan" is false for INTEGER PRIMARY KEY rowid
 * lookups — REINSTATED below with corrected, honest framing that doesn't make
 * that blanket claim), and `schema_object_counts` (static inventory, not a
 * live signal — STILL cut here, unchanged).
 *
 * Two more `indexes`-category templates were added to give the S21 Health
 * pack (categories `performance` + `indexes`) real coverage instead of the
 * single `index_inventory` template it had before: `unindexed_foreign_keys`
 * (SQLite never auto-indexes FK columns, unlike most other engines — a
 * well-known, concrete footgun) and `index_overhead` (dbstat-derived — note
 * dbstat's aggregate mode still walks every b-tree page in the file, so it
 * carries the same whole-file-scan caveat as `integrity_check`'s quick_check,
 * not a cheap catalog-metadata read).
 */
import type { DashboardTemplate } from '../templateTypes';

export const sqliteTemplates: DashboardTemplate[] = [
  {
    id: 'sqlite.storage.database_size',
    engine: 'sqlite',
    title: 'Main database file size',
    description: "Main database's on-disk footprint, computed from page count × page size — the number that matters for disk budgeting. Does not include the WAL/SHM sidecar files' own on-disk size while WAL mode is active.",
    category: 'storage',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'database_size_bytes',
    expectedShape: 'scalar',
    emptyStateCopy: 'Could not read page-count/page-size pragmas.',
    sql: `SELECT
  pc.page_count * ps.page_size AS database_size_bytes
FROM pragma_page_count() pc, pragma_page_size() ps
WHERE {{dashboard_filters}}`,
  },
  {
    id: 'sqlite.storage.freelist_pages',
    engine: 'sqlite',
    title: 'Reclaimable free pages',
    description: 'Free pages left behind by deletes, as a share of the total file — VACUUM reclaims this space; a high percentage means the file is bigger on disk than it needs to be.',
    category: 'storage',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'freelist_pct',
    expectedShape: 'scalar',
    emptyStateCopy: 'No reclaimable free pages — the file is fully packed.',
    sql: `SELECT
  fc.freelist_count,
  pc.page_count,
  ROUND(100.0 * fc.freelist_count / NULLIF(pc.page_count, 0), 1) AS freelist_pct
FROM pragma_freelist_count() fc, pragma_page_count() pc
WHERE fc.freelist_count > 0
  AND {{dashboard_filters}}`,
  },
  {
    id: 'sqlite.storage.integrity_check',
    engine: 'sqlite',
    title: 'Integrity check',
    description: 'Runs SQLite\'s quick structural check (faster than the full integrity check, skips index-content cross-verification) — a single "ok" row means the file is healthy, extra rows each name a problem. This scans the database — prefer a manual refresh over a tight auto-refresh interval.',
    category: 'storage',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    // pragma_quick_check() ALWAYS returns at least one row — a healthy file
    // returns exactly one row containing "ok", never zero rows (M18).
    emptyStateCopy: 'This check never returns zero rows — a single "ok" row means the file is healthy; each additional row names a specific corruption found.',
    sql: `SELECT *
FROM pragma_quick_check()
WHERE {{dashboard_filters}}`,
  },
  {
    id: 'sqlite.indexes.index_inventory',
    engine: 'sqlite',
    title: 'Index inventory',
    description: 'Every index and the columns it covers — spot a table you assumed was indexed but is not.',
    category: 'indexes',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No indexes defined yet.',
    sql: `SELECT
  m.name AS index_name,
  m.tbl_name AS table_name,
  ii.name AS column_name,
  ii.seqno
FROM sqlite_master m
JOIN pragma_index_info(m.name) ii
WHERE m.type = 'index'
  AND {{dashboard_filters}}
ORDER BY m.tbl_name, m.name, ii.seqno`,
  },
  {
    id: 'sqlite.indexes.tables_without_index',
    engine: 'sqlite',
    title: 'Tables with no index',
    description: "Ordinary tables with zero indexes of any kind — not even an implicit one from a UNIQUE or PRIMARY KEY constraint. Anything other than a lookup by the table's own integer primary key falls back to a full table scan. Virtual tables and their shadow tables are excluded — they are indexless by construction, not actionable.",
    category: 'indexes',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No tables are missing an index — every table has at least one explicit, unique, or primary-key index beyond the implicit rowid.',
    // Candidate tables come from pragma_table_list (SQLite >= 3.37, well below
    // the bundled build) rather than sqlite_master: its `type` column separates
    // real tables from 'virtual' and 'shadow' entries, which sqlite_master
    // lumps together as type='table' — an FTS5 table and its `_content`/
    // `_data`/`_docsize`/... shadows would otherwise all be flagged here.
    // sqlite_schema itself surfaces as an ordinary 'table' row in 'main',
    // hence the retained sqlite_% exclusion.
    sql: `SELECT
  tl.name AS table_name
FROM pragma_table_list() tl
WHERE tl.schema = 'main'
  AND tl.type = 'table'
  AND tl.name NOT LIKE 'sqlite_%'
  AND NOT EXISTS (
    SELECT 1 FROM pragma_index_list(tl.name) il
  )
  AND {{dashboard_filters}}
ORDER BY tl.name`,
  },
  {
    id: 'sqlite.indexes.unindexed_foreign_keys',
    engine: 'sqlite',
    title: 'Foreign keys without a supporting index',
    description: "SQLite does not automatically index foreign key child columns. Flags each foreign key whose leading column is not the leading column of any non-partial index and is not the table's primary key — without one, joins on that key and parent-side cascade/delete checks can fall back to scanning the child table.",
    category: 'indexes',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: "Every foreign key's leading column already has a supporting index, or is itself the table's primary key.",
    // Checks only the FIRST (leading) column of each foreign key against each
    // table's indexes (pragma_index_info seqno=0) and primary key columns
    // (pragma_table_info pk=1, catching the invisible-to-pragma_index_list
    // INTEGER PRIMARY KEY rowid alias case) — the same leading-column
    // reasoning DBAs use for composite indexes in general. Partial indexes
    // (pragma_index_list.partial = 1) do NOT count as supporting: their WHERE
    // clause means they don't cover every child row, so joins and cascade
    // checks can't rely on them.
    sql: `WITH fk_lead AS (
  SELECT
    m.name AS table_name,
    fkl."table" AS referenced_table,
    fkl."from" AS fk_column
  FROM sqlite_master m
  JOIN pragma_foreign_key_list(m.name) fkl
  WHERE m.type = 'table'
    AND fkl.seq = 0
),
leading_index_cols AS (
  SELECT DISTINCT
    m.name AS table_name,
    ii.name AS column_name
  FROM sqlite_master m
  JOIN pragma_index_list(m.name) il
  JOIN pragma_index_info(il.name) ii ON ii.seqno = 0
  WHERE m.type = 'table'
    AND il.partial = 0
),
pk_cols AS (
  SELECT m.name AS table_name, ti.name AS column_name
  FROM sqlite_master m
  JOIN pragma_table_info(m.name) ti ON ti.pk = 1
  WHERE m.type = 'table'
)
SELECT
  fk_lead.table_name,
  fk_lead.fk_column,
  fk_lead.referenced_table
FROM fk_lead
WHERE NOT EXISTS (
  SELECT 1 FROM leading_index_cols lic
  WHERE lic.table_name = fk_lead.table_name AND lic.column_name = fk_lead.fk_column
)
AND NOT EXISTS (
  SELECT 1 FROM pk_cols pk
  WHERE pk.table_name = fk_lead.table_name AND pk.column_name = fk_lead.fk_column
)
AND {{dashboard_filters}}
ORDER BY fk_lead.table_name, fk_lead.fk_column`,
  },
  {
    id: 'sqlite.indexes.index_overhead',
    engine: 'sqlite',
    title: 'Index overhead',
    description: "Bytes in index b-tree pages as a share of the database file's TOTAL size (page count × page size, freelist and overhead pages included) — a very high share can mean over-indexing for how the data is actually queried. Computed via dbstat, which walks every b-tree page in the file: on large databases prefer a long refresh interval or a manual refresh, same as the integrity check.",
    category: 'indexes',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'index_overhead_pct',
    expectedShape: 'scalar',
    // The pragma_page_count() × pragma_page_size() cross join always yields
    // exactly one row, and COALESCE folds the schema-less case (no index
    // b-trees at all, or a zero-page file) to a numeric 0 — "0 rows" or a
    // NULL stat can never happen, same shape note as the other stat templates.
    emptyStateCopy: 'Always returns one row — there is no universally "healthy" share, but a percentage climbing well past what a similar schema normally needs is worth a look.',
    // Denominator is the WHOLE file (pragma_page_count × pragma_page_size),
    // not the sum of live b-tree pages dbstat can see: dbstat covers neither
    // freelist nor pointer-map/overhead pages, and joining it to sqlite_master
    // also drops the sqlite_schema b-tree — against a small churned file that
    // narrower denominator overstated a single 4KiB index ~3x (measured 25%
    // vs the true 7.7% on the e2e fixture).
    sql: `SELECT
  ROUND(COALESCE(
    100.0 * (
      SELECT SUM(st.pgsize)
      FROM dbstat st
      WHERE st.aggregate = TRUE
        AND st.name IN (SELECT name FROM sqlite_master WHERE type = 'index')
    ) / NULLIF(pc.page_count * ps.page_size, 0),
    0.0), 1) AS index_overhead_pct
FROM pragma_page_count() pc, pragma_page_size() ps
WHERE {{dashboard_filters}}`,
  },
];
